import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Radio, Link2, KeyRound, Terminal, CheckCircle2,
  Copy, Loader2, Activity, ExternalLink, Server, ShieldOff, Download,
} from 'lucide-react';
import RigManagerPanel from '@/components/admin/RigManagerPanel';
import LockedField from '@/components/admin/LockedField';
import { useAuth } from '@/lib/AuthContext';

const LS_KEY = 'mr_ingest_url';
const LS_TOKEN = 'mr_telemetry_token';
const ENDPOINT_SUFFIX = '/functions/ingestTelemetry';
const DEFAULT_ENDPOINT = `${window.location.origin}${ENDPOINT_SUFFIX}`;

const validateEndpoint = (v) => {
  if (!v) return 'Enter the ingest endpoint URL.';
  if (!/^https?:\/\/\S+\/functions\/ingestTelemetry$/.test(v)) {
    return 'Must be a full URL ending in /functions/ingestTelemetry.';
  }
  return '';
};

const validateToken = (v) => (v ? '' : 'Enter the telemetry token before saving.');

export default function AdminLiveTelemetry() {
  const { user, isLoadingAuth: authLoading } = useAuth();
  const [ingestUrl, setIngestUrl] = useState('');
  const [token, setToken] = useState('');
  const [copied, setCopied] = useState('');
  const [rigs, setRigs] = useState([]);
  const [loadingRigs, setLoadingRigs] = useState(true);
  const [loadError, setLoadError] = useState('');

  const feedUrl = `${window.location.origin}/live`;

  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY) || '';
    // Fall back to this app's own function URL when nothing valid is stored,
    // so a corrupted or missing value never leaves the setup blank.
    setIngestUrl(validateEndpoint(stored) ? DEFAULT_ENDPOINT : stored);
    setToken(localStorage.getItem(LS_TOKEN) || '');
  }, []);

  const loadRigs = () => {
    base44.entities.RigLiveFeed.list('-sort_order', 50)
      .then((d) => { setRigs((d || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))); setLoadError(''); })
      .catch((e) => setLoadError(e.message || 'Could not load rig status'))
      .finally(() => setLoadingRigs(false));
  };
  useEffect(() => {
    loadRigs();
    const unsub = base44.entities.RigLiveFeed.subscribe((event) => {
      setRigs((prev) => {
        if (event.type === 'delete') return prev.filter((r) => r.id !== event.id);
        const idx = prev.findIndex((r) => r.id === event.id);
        const sortByOrder = (a, b) => (a.sort_order || 0) - (b.sort_order || 0);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], ...event.data };
          return [...next].sort(sortByOrder);
        }
        return [...prev, event.data].sort(sortByOrder);
      });
    });
    return unsub;
  }, []);

  const confirmIngestUrl = (v) => {
    setIngestUrl(v);
    localStorage.setItem(LS_KEY, v);
  };

  const confirmToken = (v) => {
    setToken(v);
    localStorage.setItem(LS_TOKEN, v);
  };

  const copy = (text, key) => {
    navigator.clipboard?.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const envBlock = `# === Motion Reality — Live Telemetry Pusher env vars ===
# Windows:  set X=Y      (Command Prompt)   or  $env:X="Y"  (PowerShell)
# macOS/Linux: export X=Y

TELEMETRY_TOKEN=${token || '<paste the LIVE_TELEMETRY_TOKEN value>'}
INGEST_URL=${ingestUrl || '<paste the ingestTelemetry endpoint URL>'}
RIG_KEY=rig_01
RIG_NAME="Sabelt AMG Rig"
SIM=auto               # auto | iracing | assetto | acc
POLL_INTERVAL=0.3`;

  const runBlock = `# 1) install the sim SDK (once)
pip install pyirsdk requests        # iRacing
#  Assetto Corsa / ACC need no extra package (Windows shared memory, read directly)

# 2) start the pusher (with the sim running)
python telemetry_pusher.py`;

  const liveCount = rigs.filter((r) => r.last_update && (Date.now() - new Date(r.last_update).getTime() < 6000)).length;

  if (authLoading) {
    return <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center"><ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-white/40 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-white font-black text-2xl uppercase tracking-tight">Live Telemetry</h1>
              <p className="text-white/40 text-xs mt-1">Connection setup for the rig-PC telemetry pusher</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">

        {/* 1. Live feed URL */}
        <Panel icon={<Link2 className="w-4 h-4 text-primary" />} title="1 · Live Feed Webpage" desc="The public real-time dashboard. Open this on the venue screens.">
          <Field label="Live feed URL" value={feedUrl} onCopy={() => copy(feedUrl, 'feed')} copied={copied === 'feed'} />
          <a href={feedUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 text-primary text-xs font-bold tracking-widest hover:underline">
            OPEN LIVE FEED <ExternalLink className="w-3 h-3" />
          </a>
          <p className="text-white/30 text-[11px] mt-2">No login required — anyone with the link can view it.</p>
        </Panel>

        {/* 2. Ingest endpoint */}
        <Panel icon={<Server className="w-4 h-4 text-primary" />} title="2 · Ingest Endpoint (function URL)" desc="Each rig PC POSTs telemetry to this address.">
          <LockedField
            label="ingestTelemetry function URL"
            value={ingestUrl}
            onSave={confirmIngestUrl}
            validate={validateEndpoint}
            placeholder={DEFAULT_ENDPOINT}
            emptyHint="Not set — click Edit to enter it"
            resetLabel="RESET TO DEFAULT"
            onReset={() => confirmIngestUrl(DEFAULT_ENDPOINT)}
            helper="This is this app's own function URL, so there is nothing to look up in the dashboard. The field is locked against stray typing — click Edit only when it needs to change."
          />
        </Panel>

        {/* 3. Auth token */}
        <Panel icon={<KeyRound className="w-4 h-4 text-primary" />} title="3 · Auth Token" desc="The shared secret that authenticates each rig PC.">
          <div className="flex items-start gap-2.5 bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
            <KeyRound className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-white/60 text-[12px] leading-relaxed">
              The token is the <span className="text-white font-mono font-bold">LIVE_TELEMETRY_TOKEN</span> app secret you set in
              <span className="text-white"> dashboard → app settings → environment / secrets</span>. Set the same value on each rig PC
              as the <span className="text-white font-mono">TELEMETRY_TOKEN</span> environment variable — held masked below so browser
              autofill can't overwrite it.
            </p>
          </div>
          <div className="mt-4">
            <LockedField
              label="LIVE_TELEMETRY_TOKEN value (this browser)"
              value={token}
              onSave={confirmToken}
              validate={validateToken}
              mask
              emptyHint="Not set on this browser — click Edit to enter it"
              helper="Stored in this browser only, so it never has to be pasted twice: it fills the command block below and the rig setup .zip. Click Edit to change it."
            />
          </div>
        </Panel>

        {/* 4. Per-rig setup */}
        <Panel icon={<Terminal className="w-4 h-4 text-primary" />} title="4 · Set Up Each Rig PC" desc="Run these once per rig (adjust RIG_KEY, RIG_NAME and SIM per rig).">
          <CopyBlock label="Environment variables" text={envBlock} onCopy={() => copy(envBlock, 'env')} copied={copied === 'env'} />
          <div className="mt-4" />
          <CopyBlock label="Install + run" text={runBlock} onCopy={() => copy(runBlock, 'run')} copied={copied === 'run'} />
          <a href="/telemetry_pusher.py" download="telemetry_pusher.py" className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[11px] font-bold tracking-widest px-3 py-2.5 transition-colors">
            <Download className="w-3.5 h-3.5" /> DOWNLOAD PUSHER SCRIPT (v2.4)
          </a>
          <p className="text-white/30 text-[11px] mt-3">
            Download the latest pusher script and place a copy on each rig PC. Each rig should use a unique
            <span className="text-white/50 font-mono"> RIG_KEY</span> (rig_01 … rig_10) matching the slots below.
          </p>
        </Panel>

        {/* 5. Live rig status */}
        <Panel icon={<Radio className="w-4 h-4 text-primary" />} title="5 · Rig Status" desc={`${liveCount} of ${rigs.length || 10} rigs broadcasting`}>
          {loadingRigs ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
          ) : loadError ? (
            <div className="border border-red-500/20 bg-red-500/5 text-red-400 text-xs px-4 py-3">{loadError}</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {rigs.map((r) => {
                const live = r.last_update && (Date.now() - new Date(r.last_update).getTime() < 6000);
                return (
                  <div key={r.id} className={`border px-3 py-2.5 ${live ? 'border-primary/40 bg-primary/5' : 'border-white/10 bg-black/30'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-mono text-[11px] font-bold">{r.rig_key}</span>
                      {live ? (
                        <span className="flex items-center gap-1 text-primary text-[9px] font-bold tracking-widest">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-ping" /> LIVE
                        </span>
                      ) : (
                        <span className="text-white/30 text-[9px] font-bold tracking-widest">STANDBY</span>
                      )}
                    </div>
                    <p className="text-white/40 text-[10px] truncate mt-1">{r.rig_name || r.rig_key}</p>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>

        {/* 6. Manage rigs */}
        <Panel icon={<Terminal className="w-4 h-4 text-primary" />} title="6 · Manage Rigs" desc="Rename rigs, set Porsche/AMG featured themes and display order.">
          <RigManagerPanel token={token} onTokenChange={confirmToken} />
        </Panel>
      </div>
    </div>
  );
}

function Panel({ icon, title, desc, children }) {
  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-white/5">
        {icon}
        <div>
          <p className="text-white font-bold text-sm">{title}</p>
          <p className="text-white/30 text-[11px] mt-0.5">{desc}</p>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value, onCopy, copied }) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest uppercase mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <code className="flex-1 bg-black/40 border border-white/10 text-white text-xs font-mono px-3 py-2.5 truncate">{value}</code>
        <button
          onClick={onCopy}
          className="shrink-0 inline-flex items-center gap-1.5 bg-primary/15 border border-primary/30 hover:bg-primary hover:border-primary text-white text-[11px] font-bold tracking-widest px-3 py-2.5 transition-colors"
        >
          {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
      </div>
    </div>
  );
}

function CopyBlock({ label, text, onCopy, copied }) {
  return (
    <div>
      <label className="block text-white/40 text-[10px] font-bold tracking-widest uppercase mb-1.5">{label}</label>
      <div className="relative">
        <pre className="bg-black/50 border border-white/10 text-white/80 text-[11px] font-mono px-4 py-3 overflow-x-auto whitespace-pre">{text}</pre>
        <button
          onClick={onCopy}
          className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-primary/15 border border-primary/30 hover:bg-primary hover:border-primary text-white text-[10px] font-bold tracking-widest px-2.5 py-1.5 transition-colors"
        >
          {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
      </div>
    </div>
  );
}